/**
 * Tech Debt - SP - Swap out react-diff-viewer for Ace Editor once we can elegantly handle diff viewing with it
 *  * I intentionally imported the necessary styling from a specific location (rather than shared) to indicate / maintain
 *    the diff viewer's intended ephemeral nature here
 */
import React, { useState, useEffect } from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { startCase } from 'lodash';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';

import {
  Stack,
  Box,
  Typography,
  Button,
  RemoveCircleOutlineIcon,
  PlusCircleIcon,
  FilledCircleIcon,
  Alert,
} from '@metarouter-private/mr-mui';

import { useDiffPipelines } from '@ion/api';

import CenteredLoader from 'app/components/CenteredLoader';
import useCompilePipeline from 'ion-experimental/app/bulk-deploy/modals/shared/useCompilePipeline';
// TODO: Move mergeCurrentAndProposed to shared directory
import mergeCurrentAndProposed from 'app/pipeline-details/PipelineIntegrationsSection/PipelineIntegrations/DeployModal/mergeCurrentAndProposed';
import styles from 'app/pipeline-details/PipelineIntegrationsSection/PipelineIntegrations/DeployModal/index.module.scss';

const diffStyles = {
  variables: {
    light: {
      codeFoldGutterBackground: '#ebebeb',
      codeFoldBackground: '#ebebeb',
      codeFoldContentColor: '#5c6775',
    },
  },
};

export default function BulkDeployDiffContent({ pipelineId, writekey, clusterId, integrations }) {
  const [expandedIntegration, setExpandedIntegration] = useState(null);
  const {
    loading: loadingCompiledPipeline,
    compiledPipeline,
    error: pipelineCompilationError,
  } = useCompilePipeline(pipelineId);
  const [getPipelineDiff, pipelineDiffResponse] = useDiffPipelines();

  useEffect(() => {
    if (compiledPipeline.writeKey) {
      getPipelineDiff({
        clusterId,
        writekey,
        proposedJSON: compiledPipeline,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId, compiledPipeline.writeKey, clusterId, writekey]);

  const handleToggleIntegration = integrationId => {
    if (expandedIntegration === integrationId) {
      setExpandedIntegration(null);
    } else {
      setExpandedIntegration(integrationId);
    }
  };

  const shouldShowError = pipelineCompilationError || pipelineDiffResponse.errors;
  if (shouldShowError) {
    Sentry.captureException(pipelineCompilationError || pipelineDiffResponse.errors);

    return (
      <Alert severity="error">
        Pipeline diff generation failed. If this continues, please contact support for assistance.
      </Alert>
    );
  }

  const shouldShowLoading = loadingCompiledPipeline || !pipelineDiffResponse?.data?.proposed;
  if (shouldShowLoading) {
    return <CenteredLoader />;
  }

  const displayNames = integrations.reduce((integrationsObject, integration) => {
    integrationsObject[integration.id] = integration.name;
    return integrationsObject;
  }, {});

  const mergedIntegrations = mergeCurrentAndProposed({
    current: pipelineDiffResponse.data.current,
    proposed: pipelineDiffResponse.data.proposed,
    displayNames,
  });

  return (
    <Stack spacing={2}>
      {mergedIntegrations.map(integration => (
        <Stack key={integration.id} spacing={2}>
          <Stack spacing={2} direction="row" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <ChangeTypeIcon changeType={integration.changeType} />

              <Typography variant="subtitle1" color="secondary">
                {integration.displayName || integration.name}
              </Typography>

              <Button variant="outlined" onClick={() => handleToggleIntegration(integration.id)}>
                {expandedIntegration === integration.id ? 'Hide' : 'Show'} details
              </Button>
            </Box>
          </Stack>

          {expandedIntegration === integration.id && (
            <Box className={styles[`diffBorder${startCase(integration.changeType)}`]}>
              <Box sx={{ overflow: 'auto' }} className={styles.diffView}>
                <ReactDiffViewer
                  leftTitle={<span className={styles.diffHeading}>Currently Deployed</span>}
                  rightTitle={<span className={styles.diffHeading}>Staged for Deploy</span>}
                  oldValue={JSON.stringify(integration.current, null, 2)}
                  newValue={JSON.stringify(integration.proposed, null, 2)}
                  splitView={true}
                  styles={diffStyles}
                />
              </Box>
            </Box>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

BulkDeployDiffContent.propTypes = {
  pipelineId: PropTypes.string.isRequired,
  writekey: PropTypes.string.isRequired,
  clusterId: PropTypes.string.isRequired,
  integrations: PropTypes.array.isRequired,
};

function ChangeTypeIcon({ changeType }) {
  switch (changeType) {
    case 'adding':
    case 'updating':
      return <PlusCircleIcon color="success" />;
    case 'removing':
      return <RemoveCircleOutlineIcon color="error" />;
    default:
      return <FilledCircleIcon color="action" />;
  }
}

ChangeTypeIcon.propTypes = {
  changeType: PropTypes.string.isRequired,
};
